import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import { flowRight as compose } from 'lodash';
import s from './Safety.css';

import getSafetyPage from './getSafetyPage.graphql';
import { mergeSafetySettings, safetyPageDefaults } from '../../helpers/safetyPageDefaults';
import { withQuery } from '../../hoc/withGraphql';

const renderBoldText = (text = '') => String(text).split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
  if (part.startsWith('**') && part.endsWith('**')) {
    return <strong key={index}>{part.slice(2, -2)}</strong>;
  }

  return <React.Fragment key={index}>{part}</React.Fragment>;
});

const defaultFeatureIcons = [
  (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
    </svg>
  ),
  (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  ),
];

const Safety = ({ data = {} }) => {
  const settings = mergeSafetySettings(data.getSafetyPage);
  const heroBackgroundImage = settings.heroBackgroundImage || safetyPageDefaults.heroBackgroundImage;

  const features = [
    {
      title: settings.feature1Title,
      description: settings.feature1Description,
      icon: settings.feature1Icon,
      defaultIcon: defaultFeatureIcons[0],
      iconClass: s.featureIconBlue,
    },
    {
      title: settings.feature2Title,
      description: settings.feature2Description,
      icon: settings.feature2Icon,
      defaultIcon: defaultFeatureIcons[1],
      iconClass: s.featureIconGreen,
    },
    {
      title: settings.feature3Title,
      description: settings.feature3Description,
      icon: settings.feature3Icon,
      defaultIcon: defaultFeatureIcons[2],
      iconClass: s.featureIconBlue,
    },
  ];

  return (
    <div className={s.pageWrapper}>
      <section
        className={s.heroSection}
        style={{ backgroundImage: `url(${heroBackgroundImage})` }}
      >
        <div className={s.heroOverlay} />
        <div className={s.heroContainer}>
          <div className={s.heroContent}>
            <span className={s.heroBadge}>{settings.heroBadge}</span>
            <h1 className={s.heroTitle}>{settings.heroTitle}</h1>
            <h2 className={cx(s.heroSubtitle, s.multiline)}>
              {renderBoldText(settings.heroSubtitle)}
            </h2>
            <p className={cx(s.heroDescription, s.multiline)}>
              {renderBoldText(settings.heroDescription)}
            </p>
          </div>

          <div className={s.heroMascotWrapper}>
            <img
              src="/SiteImages/personaje-herramientas-1.svg"
              alt="Seguro Eslipin"
              className={s.heroMascot}
            />
          </div>
        </div>
      </section>

      <section className={s.featuresSection}>
        <div className={s.featuresGrid}>
          {features.map((feature, index) => (
            <div className={s.featureCard} key={index}>
              <div className={cx(s.featureIconBox, feature.iconClass)}>
                {feature.icon ? (
                  <img
                    src={feature.icon}
                    alt={feature.title}
                    className={s.featureIconImage}
                  />
                ) : feature.defaultIcon}
              </div>
              <h3 className={s.featureTitle}>{feature.title}</h3>
              <p className={cx(s.featureDescription, s.multiline)}>
                {renderBoldText(feature.description)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className={s.bottomCardSection}>
        <div className={s.bottomCard}>
          <div className={s.bottomCardContent}>
            <h2 className={cx(s.bottomCardTitle, s.multiline)}>
              {renderBoldText(settings.bottomCardTitle)}
            </h2>
            <p className={cx(s.bottomCardText, s.multiline)}>
              {renderBoldText(settings.bottomCardText)}
            </p>
          </div>

          <div className={s.bottomCardMascotWrapper}>
            <img
              src="/SiteImages/personaje-herramientas-2.svg"
              alt="Construyendo más seguridad"
              className={s.bottomCardMascot}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default compose(
  withStyles(s),
  withQuery(getSafetyPage, {
    options: {
      fetchPolicy: 'network-only',
    },
  }),
)(Safety);
